import { useAppWorkspaceStore } from './appWorkspaceStore'
import { resolveLiveTabPath } from './tabPathAliases'
import { parseTabPath } from './parseTabPath'

/**
 * Paths the USER just closed. `WorkspaceRoute` re-creates a tab that vanishes
 * while its route is still mounted (session hydrate used to wipe fresh tabs),
 * and a close is exactly such a disappearance — the route has not unmounted
 * yet when the store updates, so without this marker every close resurrects
 * the tab it just removed and the strip kept a title with no content.
 * One-shot: consumed by the first resurrect check, so navigating back to the
 * same path later opens a tab normally.
 */
const userClosedPaths = new Set<string>()

export function consumeUserClosed(path: string): boolean {
  if (!userClosedPaths.has(path)) return false
  userClosedPaths.delete(path)
  return true
}

/** Clear the marker when a route legitimately (re)opens this path. */
export function clearUserClosed(path: string): void {
  userClosedPaths.delete(path)
}

/**
 * Map a caller-supplied close path to a tab that actually exists in the store.
 * Exact → alias live path → active record of the same object → null.
 */
export function resolveCloseableTabPath(requested: string): string | null {
  if (!requested) return null
  const { tabs, activePath } = useAppWorkspaceStore.getState()
  if (tabs.some((t) => t.path === requested)) return requested

  const live = resolveLiveTabPath(requested)
  if (live !== requested && tabs.some((t) => t.path === live)) return live

  if (activePath && tabs.some((t) => t.path === activePath)) {
    const want = parseTabPath(requested)
    const active = parseTabPath(activePath)
    if (
      want?.kind === 'record' &&
      active?.kind === 'record' &&
      want.objectName &&
      want.objectName === active.objectName
    ) {
      return activePath
    }
  }
  return null
}

/**
 * Close a tab and return the path to navigate to next (active neighbour,
 * returnPath, or /dashboard). Caller performs navigate().
 */
export function closeWorkspaceTab(requested: string): string {
  const store = useAppWorkspaceStore.getState()
  const target = resolveCloseableTabPath(requested)
  if (target == null) {
    return store.activePath ?? store.returnPath ?? '/dashboard'
  }

  userClosedPaths.add(target)
  if (requested !== target) userClosedPaths.add(requested)

  const wasActive =
    store.activePath === target || store.activePath === requested
  store.closeTab(target)

  const next = useAppWorkspaceStore.getState()
  if (!wasActive) {
    return next.activePath ?? store.returnPath ?? '/dashboard'
  }
  return next.activePath ?? next.returnPath ?? '/dashboard'
}
